import { isReactComponent } from '@sureapp/wl-toggle-common/dist/services/envTypes';
import clsx from 'clsx';
import snakeCase from 'lodash/snakeCase';
import { useSelector } from 'react-redux';

import { TConfig } from '@@/auto/interfaces';
import { getQuoteData } from '@@/auto/store/selectors';
import plusSign from '@@/common/assets/plus-sign.svg';

import classes from './Logo.module.css';

export type TLogo = {
  disableClick?: boolean;
  hidePartnerLogo?: boolean;
  isLight?: boolean;
  dotColor?: 'green' | 'transparent';
  onClick?: () => void;
  'data-testid'?: string;
  overrideClasses?: {
    root?: string;
    img_wrapper?: string;
  };
} & Pick<TConfig, 'logos' | 'redirectUrls' | 'tenantDisplayName'>;

export default function Logo({
  disableClick = false,
  hidePartnerLogo = false,
  isLight,
  overrideClasses,
  onClick,
  'data-testid': dataTestIdProp,
  dotColor = 'transparent',
  logos: {
    default: DefaultLogo,
    light: LightLogo,
    title,
    toggleDefault,
    toggleLight,
  },
  redirectUrls: { logoRedirectUrl, isPartnerRedirectUrlEnabled },
  tenantDisplayName,
}: TLogo) {
  const TenantLogo = isLight ? LightLogo : DefaultLogo;
  const UnderwriterLogo = isLight ? toggleLight : toggleDefault;

  const className = clsx(
    classes.root,
    {
      [classes.green_dot]: dotColor === 'green',
    },
    overrideClasses?.root
  );
  const dataTestId =
    dataTestIdProp ?? `${snakeCase(title)}_${isLight ? 'light' : 'default'}`;

  const { partnerData: { redirectUrl } = {} } = useSelector(getQuoteData);

  function handleOnClick() {
    if (disableClick) {
      return;
    }

    if (onClick) {
      onClick();
    } else {
      window.location.href =
        isPartnerRedirectUrlEnabled && !!redirectUrl
          ? redirectUrl
          : logoRedirectUrl;
    }
  }

  if (UnderwriterLogo) {
    return (
      <div
        {...{
          className,
        }}
        data-testid={dataTestId}
      >
        <div
          className={clsx(overrideClasses?.img_wrapper, {
            [classes.partner_logo_wrapper]: !hidePartnerLogo,
          })}
        >
          <img
            className={clsx(classes.partner_logo_toggle)}
            src={UnderwriterLogo}
            alt="Toggle"
          />
          {!hidePartnerLogo && (
            <>
              <img
                className={clsx(classes.partner_logo_plus, {
                  [classes.carvana_logo_plus]: tenantDisplayName === 'Carvana',
                })}
                src={plusSign}
                alt="+"
              />
              <div
                onClick={handleOnClick}
                role="button"
                onKeyPress={() => handleOnClick()}
                tabIndex={0}
              >
                <img
                  className={clsx(classes.partner_logo_img, {
                    [classes.pointer]: !disableClick,
                  })}
                  data-testid="partner_logo"
                  src={TenantLogo as string}
                  alt=""
                />
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!isReactComponent(TenantLogo)) {
    return null;
  }

  return (
    <div className={className}>
      <div className={overrideClasses?.img_wrapper}>
        <TenantLogo
          {...{
            className: clsx(classes.tenant_logo, {
              [classes.pointer]: !disableClick,
            }),
            'data-testid': dataTestId,
            onClick: handleOnClick,
            title,
          }}
        />
      </div>
    </div>
  );
}
